/**
 * An open question, read once for both artefacts that carry them.
 *
 * A brief and a plan are different documents, but a question the agent still needs answered is
 * the same thing on either — same three wire keys, same rule for what counts. Reading it twice
 * is how the two drifted: the plan's badge said "Blocking" on a question the brief had already
 * dropped from its count.
 */
import { asString, isRecord } from '@flowx/designer/modules/ai-builder/utils/payload.util'

export type HarnessOpenQuestion = {
  question: string
  blocker: boolean
  /** Answered without being deleted — the harness keeps it on the payload, with the answer
   * beside it, and leaves it out of every count it makes. */
  resolved: boolean
  /** The answer the harness recorded, when it kept one. */
  answer?: string
}

export function toOpenQuestion(value: unknown): HarnessOpenQuestion {
  if (!isRecord(value)) {
    return { question: asString(value), blocker: false, resolved: false }
  }

  return {
    question: asString(value['question']),
    // Only an explicit `true` blocks. A missing flag must not silently interrupt approval.
    blocker: value['blocker'] === true,
    resolved: value['resolved'] === true,
    answer: asString(value['answer']) || undefined,
  }
}

/** The one rule: `resolved` is a question's third state, and a resolved question is owed
 * nothing. Read by the counts and by both render sites, so a badge cannot say "blocking" about
 * something no count agrees is outstanding. */
export function isBlocking(question: HarnessOpenQuestion): boolean {
  return question.blocker && !question.resolved
}

/** Blockers the maker still owes an answer to. */
export function blockerCount(questions: readonly HarnessOpenQuestion[]): number {
  return questions.filter(isBlocking).length
}
